import React, { useMemo, useState } from 'react'
import { History } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-primitives/popover'
import { Button, MiniButton } from '@/components/ui-primitives/small'
import { mergeClasses } from '@/helpers/styling'
import {
  countLines,
  formatDifference,
  formatSize,
  StoredVersion,
  useLocalStorageHistory,
} from '@/hooks/use-local-storage-history'
import { CodeEditor, CodeEditorMode } from './code-editor'

const SIZE_CHIP_STYLE = {
  grew: "bg-[#e4f4ea] text-[#1f6b45]",
  shrank: "bg-[#fde8e8] text-[#9b1c1c]",
}

/**
 * Earlier versions of a code field, kept in this browser. Pick one to read it, then restore or copy it.
 */
export function HistoryMenu(props: {
  mode: CodeEditorMode
  localHistoryKey: string
  value: string
  onRestore: (value: string) => void
}) {
  const [ open, setOpen ] = useState(false)
  const history = useLocalStorageHistory(props.localHistoryKey, props.value, props.onRestore)
  const [ selectedAt, setSelectedAt ] = useState<number | null>(null)
  const selected = history.versions.find(v => v.at === selectedAt) ?? history.versions[0] ?? null

  const restore = (version: StoredVersion) => {
    history.restore(version)
    setOpen(false)
  }

  return (
    <Popover open={open}
             onOpenChange={isOpen => {
               if (isOpen) setSelectedAt(null) // each opening starts at the newest version
               setOpen(isOpen)
             }}>
      <PopoverTrigger asChild>
        <MiniButton title="Earlier versions of this field" aria-label="Earlier versions of this field">
          <History size={14} />
        </MiniButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex h-[440px] w-[640px] max-w-[90vw] flex-col p-0 text-[13px]">
        <div className="flex items-baseline gap-2 border-b border-border px-3.5 py-2.5">
          <span className="font-exo font-semibold">Version history</span>
          <span className="text-xs text-muted-foreground">kept in this browser</span>
          <div className="flex-grow" />
          {history.versions.length > 0 && (
            <button type="button" className="text-xs text-[#9b1c1c] hover:underline" onClick={history.clear}>
              Clear history
            </button>
          )}
        </div>
        {history.versions.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground">
            No earlier versions yet. One is kept whenever you pause after editing, in this browser only.
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <VersionList versions={history.versions}
                         current={props.value}
                         selected={selected}
                         onSelect={version => setSelectedAt(version.at)} />
            {selected && (
              <VersionPreview version={selected}
                              mode={props.mode}
                              onRestore={() => restore(selected)} />
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

function VersionList(props: {
  versions: StoredVersion[]
  current: string
  selected: StoredVersion | null
  onSelect: (version: StoredVersion) => void
}) {
  const { current } = props
  const currentLines = useMemo(() => countLines(current), [ current ])
  return (
    <div role="listbox" aria-label="Versions" className="flex w-[210px] shrink-0 flex-col gap-0.5 overflow-auto border-r border-border p-1.5">
      <div className="flex flex-col px-2.5 py-1.5 text-muted-foreground">
        <span className="font-semibold text-foreground">Current</span>
        <span className="text-xs">{currentLines} lines · {formatSize(props.current.length)}</span>
      </div>
      {props.versions.map(version => {
        const lines = countLines(version.value)
        const difference = formatDifference(
          { lines, chars: version.value.length },
          { lines: currentLines, chars: props.current.length },
        )
        const isSelected = version.at === props.selected?.at
        return (
          <button key={version.at}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => props.onSelect(version)}
                  className={mergeClasses("flex flex-col rounded-md px-2.5 py-1.5 text-left hover:bg-muted", {
                    "bg-[#51479e]/10 ring-1 ring-inset ring-[#51479e]/30 hover:bg-[#51479e]/10": isSelected,
                  })}>
            <span className="font-medium tabular-nums">{formatWhen(version.at)}</span>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {lines} lines
              {difference && (
                <span className={mergeClasses("rounded-full px-1.5 text-[11px] font-medium",
                  difference.startsWith('+') ? SIZE_CHIP_STYLE.grew : SIZE_CHIP_STYLE.shrank)}>
                  {difference}
                </span>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function VersionPreview(props: {
  version: StoredVersion
  mode: CodeEditorMode
  onRestore: () => void
}) {
  const copy = () => void navigator.clipboard.writeText(props.version.value).catch(() => {})
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col p-2">
        <CodeEditor mode={props.mode} readonly fullHeight value={props.version.value} />
      </div>
      <div className="flex items-center gap-2 border-t border-border px-3.5 py-2.5">
        <span className="text-xs text-muted-foreground">Your current text stays in the history.</span>
        <div className="flex-grow" />
        <Button variant="outline" className="shrink-0" onClick={copy}>Copy</Button>
        <Button variant="purple" className="shrink-0" onClick={props.onRestore}>Restore this version</Button>
      </div>
    </div>
  )
}

/** "Today 14:32:05", "Yesterday 16:02:41" or a date; seconds tell apart versions kept in the same minute */
function formatWhen(at: number) {
  const date = new Date(at)
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const startOfToday = new Date().setHours(0, 0, 0, 0)
  if (at >= startOfToday) return `Today ${time}`
  if (at >= startOfToday - 24 * 60 * 60 * 1000) return `Yesterday ${time}`
  return `${date.toLocaleDateString()} ${time}`
}
